<template>
  <div v-show="popupVisible" class="draggable-popup" :style="popupStyle">
    <!-- Do not bind :style="{ 'top': this.topData, 'left': this.leftData }" -->
    <!-- Because it will error, popup move too fast, over mouse position -->
    <div ref="popupHeader" class="draggable-popup__header">
      <!-- Title -->
      <div class="header-content__container">
        <slot name="header">
          <div class="header__title">{{ title }}</div>
        </slot>
      </div>

      <slot name="actions" />

      <!-- Full Screen -->
      <ActionButton
        v-if="fullscreenable"
        title="Fullscreen"
        @click="toggleFullscreen"
      >
        <i class="mdi mdi-fullscreen" />
      </ActionButton>

      <!-- Collapse/Expand -->
      <ActionButton
        v-if="collapsable"
        :title="isExpanded ? 'Collapse' : 'Expand'"
        @click="toggleExpanded"
      >
        {{ isExpanded ? "▴" : "▾" }}
      </ActionButton>

      <!-- Close -->
      <ActionButton v-if="closable" title="Close" @click="popupVisible = false">
        &times;
      </ActionButton>
    </div>

    <!-- Content -->
    <div class="draggable-popup__content" :style="popupContentStyle">
      <slot />
    </div>
  </div>
</template>

<script>
import ActionButton from "./ActionButton.vue";

export default {
  name: "DraggablePopup",

  components: {
    ActionButton
  },

  props: {
    top: {
      type: Number,
      default: undefined
    },

    left: {
      type: Number,
      default: undefined
    },

    right: {
      type: Number,
      default: undefined
    },

    bottom: {
      type: Number,
      default: undefined
    },

    title: {
      type: String,
      default: ""
    },

    visible: {
      type: Boolean,
      default: true
    },

    closable: {
      type: Boolean,
      default: false
    },

    collapsable: {
      type: Boolean,
      default: false
    },

    fullscreenable: {
      type: Boolean,
      default: false
    },

    /**
     * Percentage of Viewport Width
     */
    width: {
      type: String,
      default: "25vw"
    },

    /**
     * Percentage of Viewport Height
     */
    height: {
      type: String,
      default: "85vh"
    },

    resize: {
      type: String,
      default: "both",
      validator: value =>
        ["none", "both", "horizontal", "vertical", "block", "inline"].indexOf(
          value
        ) !== -1
    }
  },

  data() {
    return {
      leftData: 0,
      topData: 0,

      widthData: this.width,
      heightData: this.height,

      isExpanded: true
    };
  },

  computed: {
    popupVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      }
    },

    /**
     * Only for set in event (not for updating in loop)
     */
    leftPosition: {
      get() {
        return this.leftData;
      },
      set(val) {
        if (typeof val !== "number") return;

        this.leftData = val;
        this.$el.style.left = this.leftData + "px";
      }
    },

    /**
     * Only for set in event (not for updating in loop)
     */
    topPosition: {
      get() {
        return this.topData;
      },
      set(val) {
        if (typeof val !== "number") return;

        this.topData = val;
        this.$el.style.top = this.topData + "px";
      }
    },

    popupStyle() {
      const style = {
        width: this.widthData,
        height: this.isExpanded ? this.heightData : "auto",
        resize: this.isExpanded ? this.resize : "none",
        overflow: "auto"
      };

      return style;
    },

    popupContentStyle() {
      const style = {};

      if (!this.isExpanded) {
        style.display = "none";
      }

      return style;
    }
  },

  watch: {
    top() {
      this.initPosition();
    },
    left() {
      this.initPosition();
    },
    right() {
      this.initPosition();
    },
    bottom() {
      this.initPosition();
    },
    visible() {
      this.initPosition();
    },
    width(val) {
      this.widthData = val;
    },
    height(val) {
      this.heightData = val;
    }
  },

  mounted() {
    const parentEl = this.getParentEl();

    const el = this.$el;
    const self = this;
    const header = this.$refs.popupHeader;

    let startX, startY, initialMouseX, initialMouseY;

    function mousemoveHeader(e) {
      const dx = e.clientX - initialMouseX;
      const dy = e.clientY - initialMouseY;

      // Do not set el.style.top or el.style.left via computed properties
      // Because it will error, popup move too fast, over mouse position
      // self.topPosition = startY + dy
      // self.leftPosition = startX + dx

      self.topData = startY + dy;
      self.leftData = startX + dx;

      const containerWidth = parentEl.offsetWidth;
      const popupWidth = el.offsetWidth;

      const containerHeight = parentEl.offsetHeight;
      const popupHeight = el.offsetHeight;

      self.topData = Math.min(
        Math.max(self.topData, 0),
        containerHeight - popupHeight
      );
      self.leftData = Math.min(
        Math.max(self.leftData, 0),
        containerWidth - popupWidth
      );

      el.style.top = self.topData + "px";
      el.style.left = self.leftData + "px";

      return false;
    }

    function mouseup() {
      document.removeEventListener("pointermove", mousemoveHeader);
      document.removeEventListener("pointerup", mouseup);
    }

    header.addEventListener("pointerdown", function(e) {
      startX = el.offsetLeft;
      startY = el.offsetTop;
      initialMouseX = e.clientX;
      initialMouseY = e.clientY;
      document.addEventListener("pointermove", mousemoveHeader);
      document.addEventListener("pointerup", mouseup);
      self.$emit("pointerdown", e);
      return false;
    });

    this.initPosition();
  },

  methods: {
    getParentEl() {
      return this.findParent(this.$el);
    },

    findParent(el) {
      if (
        ["relative", "absolute"].includes(
          getComputedStyle(el.parentElement).position
        )
      ) {
        return el.parentElement;
      } else if (el.tagName === "BODY") {
        return el;
      } else {
        return this.findParent(el.parentElement);
      }
    },

    centerWindow() {
      this.leftPosition = (window.screen.width - this.$el.offsetWidth) / 2;
      this.topPosition = (window.screen.height - this.$el.offsetHeight) / 2;
    },

    defaultLeft() {
      return 10;
    },

    defaultTop() {
      return 10;
    },

    getLeftFromRight() {
      return this.getParentEl().offsetWidth - this.$el.offsetWidth - this.right;
    },

    getTopFromBottom() {
      return (
        this.getParentEl().offsetHeight - this.$el.offsetHeight - this.bottom
      );
    },

    initPosition() {
      if (!this.popupVisible) {
        return;
      }

      this.$nextTick(() => {
        this.leftPosition =
          typeof this.left !== "number"
            ? typeof this.right !== "number"
              ? this.defaultLeft()
              : this.getLeftFromRight()
            : this.left;
        this.topPosition =
          typeof this.top !== "number"
            ? typeof this.bottom !== "number"
              ? this.defaultTop()
              : this.getTopFromBottom()
            : this.top;
      });
    },

    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },

    async toggleFullscreen() {
      if (document.fullscreenElement) {
        await this.closeFullscreen();
        this.initPosition();
      } else {
        await this.openFullscreen(this.$el);
      }
    },

    /* View in fullscreen */
    openFullscreen(elem) {
      if (elem.requestFullscreen) {
        return elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Safari */
        return elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE11 */
        return elem.msRequestFullscreen();
      }
    },

    /* Close fullscreen */
    closeFullscreen() {
      if (document.exitFullscreen) {
        return document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        /* Safari */
        return document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE11 */
        return document.msExitFullscreen();
      }
    }
  }
};
</script>

<style scoped>
.draggable-popup {
  position: absolute;
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 1px 5px 0 rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background-color: white;
  pointer-events: auto;
  display: flex;
  flex-flow: column;
}

.draggable-popup__header {
  padding: 8px 10px;
  cursor: move;
  flex: none;
  display: flex;
  align-items: center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.header-content__container {
  flex: 1;
  min-width: 0;
}

.header__title {
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.draggable-popup__content {
  position: relative;
  flex: 1;
  min-height: 0;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow: auto;
}

@media screen and (max-width: 768px) {
  .draggable-popup {
    width: 100%;
  }

  .draggable-popup__content {
    width: 100% !important;
    resize: vertical !important;
  }
}
</style>
